"use client";

import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger
} from "@nextui-org/react";
import {FiChevronDown, FiChevronRight} from "react-icons/fi";

type SosCurrentTab = "active" | "resolved" | "falseAlarm";

export type SosUser = {
  id: string;
  name: string;
  bookingId: string;
  avatar: string;
  phone: string;
  boardingPoint: string;
  dropPoint: string;
  reason: string;
  travelFrom: string;
  travelTo: string;
  travelDate: string;
  totalTravelTime: string;
};

export type SosCardItem = {
  id: string;
  busNo: string;
  pickupTime: string;
  caretakerName: string;
  driverCode: string;
  driverPh: string;
  from: string;
  to: string;
  status: 1 | 2 | 3;
  users: SosUser[];
};

const getActionButtonConfig = (tab: SosCurrentTab) => {
  switch (tab) {
    case "resolved":
      return {
        label: "Resolved",
        className:
          "h-9 min-w-[132px] rounded-xl bg-green-500 px-3 text-xs font-semibold text-white shadow-none hover:opacity-95",
        items: [{key: "cancelled", label: "Cancelled"}]
      };
    case "falseAlarm":
      return {
        label: "Cancelled",
        className:
          "h-9 min-w-[132px] rounded-xl bg-black px-3 text-xs font-semibold text-white shadow-none hover:opacity-95",
        items: [{key: "resolved", label: "Resolved"}]
      };
    default:
      return {
        label: "Take Action",
        className:
          "h-9 min-w-[132px] rounded-xl bg-rose-500 px-3 text-xs font-semibold text-white shadow-none hover:opacity-95",
        items: [
          {key: "resolved", label: "Resolved"},
          {key: "cancelled", label: "Cancelled"}
        ]
      };
  }
};

export const SOSDetailView = ({
  card,
  currentTab = "active",
  onStatusChange,
  onClose: _onClose
}: {
  card: SosCardItem;
  currentTab?: SosCurrentTab;
  onStatusChange: (
    cardId: string,
    userId: string,
    nextStatus: 1 | 2 | 3
  ) => void;
  onClose?: () => void;
}) => {
  const actionButton = getActionButtonConfig(currentTab);
  const firstUser = card.users[0];
  const routeName = `Route ${Number(card.users[0]?.bookingId ?? 0) % 12 || 4}`;
  const visibleUsers = card.users;

  return (
    <div className="flex min-h-[calc(100vh-63px)] flex-col px-2 py-2">
      <div className=" pb-2">
        <div className=" font-semibold ">SOS Details</div>
      </div>

      <div className="mt-2 w-72 rounded-xl border border-slate-200 p-4">
        <div className="font-semibold ">Trip Summary</div>
        <div className="mt-2 rounded-xl bg-white px-2 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
          <div className="flex flex-row gap-4">
            <div>
              <div className="text-xs font-medium">Bus</div>
              <div className="mt-2 flex items-center gap-2">
                <div className="text-[15px] font-semibold text-slate-800">
                  {card.busNo} / {routeName}
                </div>
              </div>
            </div>

            <div>
              <div className="text-xs font-medium ">Trip Timing</div>
              <div className="mt-2 text-[15px] font-semibold text-slate-800">
                {firstUser
                  ? `${firstUser.travelFrom} - ${firstUser.travelTo}`
                  : "-"}
              </div>
            </div>

            <div>
              <div className="text-xs font-medium ">Route</div>
              <div className="mt-2 text-[15px] font-semibold text-slate-800">
                {`${card.from} -> ${card.to}`}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-xs font-semibold">User Actions</div>
          </div>
          <div className="text-xs font-medium text-slate-600">
            Total users: {visibleUsers.length}
          </div>
        </div>

        <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
          {visibleUsers.length > 0 ? (
            <>
              <div className="hidden grid-cols-[1fr_0.8fr_1.05fr_1.05fr_1.35fr_0.9fr_0.9fr_0.95fr] gap-3 bg-slate-50 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 md:grid">
                <div>User</div>
                <div>Booking ID</div>
                <div>Boarding Point</div>
                <div>Drop Point</div>
                <div>Reason</div>
                <div>Travel</div>
                <div>User No</div>
                <div>Action</div>
              </div>

              {visibleUsers.map(user => (
                <div
                  key={user.id}
                  className="grid gap-2.5 border-t border-slate-200 px-3 py-3 first:border-t-0 md:grid-cols-[1fr_0.8fr_1.05fr_1.05fr_1.35fr_0.9fr_0.9fr_0.95fr] md:items-center"
                >
                  <div className="text-xs font-semibold text-slate-800">
                    {user.name}
                  </div>
                  <div className="text-xs text-slate-600">{user.bookingId}</div>
                  <div className="text-xs text-slate-600">
                    {user.boardingPoint}
                  </div>
                  <div className="text-xs text-slate-600">{user.dropPoint}</div>
                  <div className="text-xs text-slate-600">{user.reason}</div>
                  <div className="text-xs text-slate-600">
                    <div className="font-medium text-slate-700">
                      {user.travelFrom} - {user.travelTo}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500">
                      {user.travelDate} | {user.totalTravelTime}
                    </div>
                  </div>
                  <div className="text-xs text-slate-600">{user.phone}</div>
                  <div>
                    <Dropdown>
                      <DropdownTrigger>
                        <Button size="sm" className={actionButton.className}>
                          <span>{actionButton.label}</span>
                          <FiChevronDown className="h-3.5 w-3.5 opacity-90" />
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu
                        aria-label="Rider actions"
                        onAction={key => {
                          if (key === "resolved") {
                            onStatusChange(card.id, user.id, 2);
                          }
                          if (key === "cancelled") {
                            onStatusChange(card.id, user.id, 3);
                          }
                        }}
                      >
                        <DropdownSection title="Status" showDivider>
                          {actionButton.items.map(item => (
                            <DropdownItem
                              key={item.key}
                              endContent={
                                <FiChevronRight className="h-4 w-4 text-slate-700" />
                              }
                            >
                              {item.label}
                            </DropdownItem>
                          ))}
                        </DropdownSection>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="px-4 py-8 text-center text-xs text-slate-500">
              No users matched your filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
